import React, { useState } from 'react'
import * as XLSX from 'xlsx'
import { getSessionHeaders } from '../../lib/serverSession'

function csvEscape(value: unknown) {
  const s = value == null ? '' : String(value)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function toStartIso(dateStr: string) {
  if (!dateStr) return null
  const d = new Date(`${dateStr}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

function toEndIso(dateStr: string) {
  if (!dateStr) return null
  const d = new Date(`${dateStr}T23:59:59.999`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

function flattenCountryRows(countries: CountryBucket[]) {
  const summary = countries.map((c) => ({
    country: c.country,
    user_count: c.user_count,
    purchase_count: c.purchase_count,
    shares_purchased: c.shares_purchased,
    bonus_shares: c.bonus_shares,
    total_amount: c.total_amount,
    phases: c.phases.map((p) => `${p.phase}: ${p.shares}`).join(' | '),
  }))
  const users = countries.flatMap((c) =>
    c.users.map((u) => ({
      country: c.country,
      user_id: u.user_id,
      username: u.username,
      email: u.email,
      cellphone: u.cellphone,
      shares_purchased: u.shares_purchased,
      bonus_shares: u.bonus_shares,
      total_amount: u.total_amount,
      phases: u.phases.map((p) => `${p.phase}: ${p.shares}`).join(' | '),
    })),
  )
  return { summary, users }
}

interface CountryBucket {
  country: string
  user_count: number
  purchase_count: number
  shares_purchased: number
  bonus_shares: number
  total_amount: number
  phases: Array<{ phase: string; shares: number; bonus: number; amount: number; purchases: number }>
  users: Array<{
    user_id: string | number
    username: string
    email: string
    cellphone: string
    shares_purchased: number
    bonus_shares: number
    total_amount: number
    phases: Array<{ phase: string; shares: number }>
  }>
}

interface CountryShareAuditPanelProps {
  onBack: () => void
}

export function CountryShareAuditPanel({ onBack }: CountryShareAuditPanelProps) {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [countries, setCountries] = useState<CountryBucket[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)

  async function run() {
    setLoading(true)
    setError(null)
    try {
      const headers = await getSessionHeaders()
      const res = await fetch('/api/admin/country-share-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          operation: 'load_country_audit',
          data: {
            startDate: toStartIso(startDate),
            endDate: toEndIso(endDate),
          },
        }),
      })
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setCountries(json.countries || [])
      setExpanded(null)
    } catch (e: any) {
      setError(e?.message || 'Country audit failed')
      setCountries([])
    } finally {
      setLoading(false)
    }
  }

  function downloadCsv() {
    const { summary, users } = flattenCountryRows(countries)
    const summaryCols = ['country', 'user_count', 'purchase_count', 'shares_purchased', 'bonus_shares', 'total_amount', 'phases'] as const
    const userCols = ['country', 'user_id', 'username', 'email', 'cellphone', 'shares_purchased', 'bonus_shares', 'total_amount', 'phases'] as const
    const summaryLines = [summaryCols.join(','), ...summary.map((row) => summaryCols.map((k) => csvEscape(row[k])).join(','))]
    const userLines = [userCols.join(','), ...users.map((row) => userCols.map((k) => csvEscape(row[k])).join(','))]
    const body = ['Countries', ...summaryLines, '', 'Users', ...userLines].join('\n')
    const blob = new Blob(['\ufeff' + body], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `country-share-audit-${startDate || 'all'}-${endDate || 'all'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function downloadXlsx() {
    const { summary, users } = flattenCountryRows(countries)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), 'Countries')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(users), 'Users')
    XLSX.writeFile(wb, `country-share-audit-${startDate || 'all'}-${endDate || 'all'}.xlsx`)
  }

  return (
    <div style={{ color: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>Country audits</h2>
          <p style={{ margin: '6px 0 0', color: '#9ca3af', fontSize: 13 }}>
            Group users by country with shares purchased and phases (optional date filter).
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', cursor: 'pointer' }}
        >
          Change tool
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'end', marginBottom: 14, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#9ca3af' }}>
          Start (optional)
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ padding: 10, borderRadius: 6, border: '1px solid #333', background: '#0a0a0a', color: '#fff' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#9ca3af' }}>
          End (optional)
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ padding: 10, borderRadius: 6, border: '1px solid #333', background: '#0a0a0a', color: '#fff' }}
          />
        </label>
        <button
          type="button"
          disabled={loading}
          onClick={run}
          style={{ padding: '10px 14px', borderRadius: 6, border: 'none', background: '#059669', color: '#fff', cursor: 'pointer' }}
        >
          {loading ? 'Loading…' : 'Run country audit'}
        </button>
        <button
          type="button"
          disabled={!countries.length}
          onClick={downloadCsv}
          style={{ padding: '10px 14px', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff', cursor: countries.length ? 'pointer' : 'not-allowed' }}
        >
          Export CSV
        </button>
        <button
          type="button"
          disabled={!countries.length}
          onClick={downloadXlsx}
          style={{ padding: '10px 14px', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff', cursor: countries.length ? 'pointer' : 'not-allowed' }}
        >
          Export XLSX
        </button>
      </div>

      {error && <div style={{ color: '#fca5a5', marginBottom: 10 }}>{error}</div>}

      <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #222', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#111', textAlign: 'left' }}>
              {['Country', 'Users', 'Purchases', 'Shares', 'Bonus', 'Amount $', 'Phases', ''].map((h) => (
                <th key={h || 'x'} style={{ padding: '8px 10px', borderBottom: '1px solid #222' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {countries.map((c) => (
              <React.Fragment key={c.country}>
                <tr>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a', fontWeight: 600 }}>{c.country}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{c.user_count}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{c.purchase_count}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{c.shares_purchased}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{c.bonus_shares}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>{c.total_amount}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>
                    {c.phases.map((p) => `${p.phase}: ${p.shares}`).join(' · ')}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #1a1a1a' }}>
                    <button
                      type="button"
                      onClick={() => setExpanded(expanded === c.country ? null : c.country)}
                      style={{ background: 'transparent', border: '1px solid #333', color: '#fff', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' }}
                    >
                      {expanded === c.country ? 'Hide users' : 'Users'}
                    </button>
                  </td>
                </tr>
                {expanded === c.country && (
                  <tr>
                    <td colSpan={8} style={{ padding: 0, background: '#0a0a0a' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr>
                            {['Username', 'Email', 'Cell', 'Shares', 'Bonus', 'Amount', 'Phases'].map((h) => (
                              <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: '#9ca3af', borderBottom: '1px solid #1a1a1a' }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {c.users.map((u) => (
                            <tr key={String(u.user_id)}>
                              <td style={{ padding: '6px 10px', borderBottom: '1px solid #151515' }}>{u.username}</td>
                              <td style={{ padding: '6px 10px', borderBottom: '1px solid #151515' }}>{u.email}</td>
                              <td style={{ padding: '6px 10px', borderBottom: '1px solid #151515' }}>{u.cellphone}</td>
                              <td style={{ padding: '6px 10px', borderBottom: '1px solid #151515' }}>{u.shares_purchased}</td>
                              <td style={{ padding: '6px 10px', borderBottom: '1px solid #151515' }}>{u.bonus_shares}</td>
                              <td style={{ padding: '6px 10px', borderBottom: '1px solid #151515' }}>{u.total_amount}</td>
                              <td style={{ padding: '6px 10px', borderBottom: '1px solid #151515' }}>
                                {u.phases.map((p) => `${p.phase}: ${p.shares}`).join(' · ')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!countries.length && !loading && (
              <tr>
                <td colSpan={8} style={{ padding: 20, color: '#9ca3af', textAlign: 'center' }}>
                  Run country audit to load grouped share purchases.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
